import React, { useMemo } from 'react';
import { useRouter } from 'next/router';
import sanitizer from '@helpers/sanitizer';
import { Breadcrumb } from '@interfaces/models/breadcrumb';
import { getSeoBaseUrlFromLocale } from '@helpers/utils/seo-structured-data';
import { BreadcrumbList, WithContext } from 'schema-dts';

type BreadcrumbSchemaProps = {
  breadcrumbs: Breadcrumb[];
} & React.HTMLProps<HTMLDivElement>;

const buildItemUrl = (path: string, origin: string): string => (origin ? `${origin}${path}` : path);

export const createBreadcrumbSchema = (breadcrumbs: Breadcrumb[], origin = ''): WithContext<BreadcrumbList> => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: breadcrumbs.map((breadcrumb: Breadcrumb, index: number) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: breadcrumb.localizedName,
    item: buildItemUrl(breadcrumb.url.path, origin),
  })),
});

const BreadcrumbSchema: React.FC<BreadcrumbSchemaProps> = (props): React.JSX.Element => {
  const { breadcrumbs } = props;
  const { locale } = useRouter();
  const origin = getSeoBaseUrlFromLocale(locale);

  const schema: WithContext<BreadcrumbList> = useMemo(
    () => createBreadcrumbSchema(breadcrumbs, origin),
    [breadcrumbs, origin],
  );

  return (
    <script
      data-cy="breadcrumbs-schema"
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: sanitizer(JSON.stringify(schema)),
      }}
    />
  );
};

export default BreadcrumbSchema;
